import React from 'react';
import { ReviewCycle, ReviewCycleStatus } from '../../types/pms';
import './ReviewCycleSelector.css';

interface ReviewCycleSelectorProps {
  cycles: ReviewCycle[];
  selectedCycleId: string;
  onChange: (cycleId: string) => void;
  filterDescription?: string;
  /** Label override for the cycle selector */
  label?: string;
  emptyLabel?: string;
  /**
   * Annual objective cycle id → display name, used to group the options by parent.
   *
   * The list is every ACTIVE child cycle in the system, which in practice spans many annual
   * objective cycles at once — so names like "end q1" and "26_27_Yearly" sat side by side in one
   * flat list with nothing to say they belong to different years. Grouping restores the
   * ANNUAL CYCLE → REVIEW CYCLE relationship in the one place a user picks between them.
   * Omit it and the dropdown renders flat exactly as before.
   */
  annualCycleNames?: Record<string, string>;
}

/**
 * Returns true when the selected cycle is not the active one (i.e. historical).
 */
export function isHistoricalCycle(
  cycles: ReviewCycle[],
  selectedCycleId: string
): boolean {
  const selected = cycles.find((c) => c.id === selectedCycleId);
  return !!selected && selected.status !== ReviewCycleStatus.ACTIVE;
}

function getStatusSuffix(status: ReviewCycleStatus): string {
  switch (status) {
    case ReviewCycleStatus.ACTIVE:
      return ' (Active)';
    case ReviewCycleStatus.COMPLETED:
      return ' (Closed)';
    case ReviewCycleStatus.LOCKED:
      return ' (Locked)';
    case ReviewCycleStatus.DRAFT:
      return ' (Upcoming)';
    default:
      return '';
  }
}

/**
 * ReviewCycleSelector Component
 * Dropdown to switch between configured cycles.
 * Shows status suffix and a read-only banner when a historical cycle is selected.
 */
const ReviewCycleSelector: React.FC<ReviewCycleSelectorProps> = ({
  cycles,
  selectedCycleId,
  onChange,
  filterDescription,
  label = 'Review Cycle',
  emptyLabel = 'No review cycles available',
  annualCycleNames,
}) => {
  const historical = isHistoricalCycle(cycles, selectedCycleId);
  const hasCycles = cycles.length > 0;
  /**
   * Options grouped by parent annual cycle, preserving the order they arrived in.
   *
   * Only used when the caller supplies names and more than one parent is actually present —
   * a single-parent list gains nothing from a group header, and grouping cycles whose parent
   * name is unknown would replace a real name with a placeholder.
   */
  const groups = React.useMemo(() => {
    if (!annualCycleNames) return null;
    const byParent = new Map<string, ReviewCycle[]>();
    for (const cycle of cycles) {
      const key = cycle.annualCycleId || '';
      const existing = byParent.get(key);
      if (existing) existing.push(cycle);
      else byParent.set(key, [cycle]);
    }
    /*
     * Group whenever we can name a parent — a single-parent list included.
     *
     * This used to bail at `size < 2` on the reasoning that one group header adds nothing. But
     * the header is what shows the objective cycle the reviews hang off, and once the list is
     * narrowed to the current year one parent is the NORMAL case, not a degenerate one. Bailing
     * there meant the hierarchy disappeared exactly when it had finally become readable.
     */
    if (byParent.size === 0) return null;
    return Array.from(byParent.entries()).map(([parentId, items]) => ({
      parentId,
      label: annualCycleNames[parentId] || 'Unassigned objective cycle',
      items,
    }));
  }, [cycles, annualCycleNames]);

  return (
    <div className="pms-cycle-selector">
      <label htmlFor="pms-cycle-select" className="pms-cycle-selector__label">
        {label}
      </label>
      <select
        id="pms-cycle-select"
        className={`pms-cycle-selector__select${historical ? ' pms-cycle-selector__select--historical' : ''}`}
        value={selectedCycleId}
        onChange={(e) => onChange(e.target.value)}
        disabled={!hasCycles}
      >
        {hasCycles ? (
          groups ? (
            groups.map((group) => (
              <optgroup key={group.parentId || 'unassigned'} label={group.label}>
                {group.items.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name}{getStatusSuffix(cycle.status)}
                  </option>
                ))}
              </optgroup>
            ))
          ) : (
            cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.name}{getStatusSuffix(cycle.status)}
              </option>
            ))
          )
        ) : (
          <option value="">{emptyLabel}</option>
        )}
      </select>
      {historical && (
        <span className="pms-cycle-selector__badge" aria-label="Viewing historical data">
          {'\u{1F4CB}'} Read-only
        </span>
      )}
      {filterDescription && (
        <span className="pms-cycle-selector__description" title={filterDescription}>
          {filterDescription}
        </span>
      )}
    </div>
  );
};

export default ReviewCycleSelector;
